/**
 * SlideCarousel — the ONE card-carousel mechanic (PR-B2 extraction).
 *
 * Touch-swipe / hover-arrow / dot-rail behavior matching the Rails
 * CardCarouselComponent, factored out of the listings `CardCarousel` (the
 * estate index cards used it too until D14 retired their stored Google
 * photos). Content-agnostic:
 * callers supply fully-rendered slides; this component owns only
 * navigation, transform, and indicators.
 *
 * Used by: the sibling CardCarousel (S3 listing photos).
 *
 * ★ THE ARROWS ARE DERIVED FROM THE INDEX, AND THEY DO NOT WRAP (obs. 9). What
 * the owner saw on a card's FIRST photo was a LEFT arrow and no right one, with
 * the right one appearing only when the pointer happened to be where it would
 * be. Two causes, and the second is why this component no longer tracks its own
 * hover at all:
 *
 *   1. Wrap-around. `prev` on index 0 went to the last slide, so a "previous"
 *      arrow existed on the first photo — which reads as "there is something to
 *      the left of the first photo", and there is not. Standard behaviour is no
 *      wrap: at index 0 only "next", at the last index only "prev", and the
 *      touch swipe clamps to the same bounds.
 *   2. A DEAD `isHovering` state. The next arrow's class list carried
 *      `isHovering ? 'opacity-100' : 'opacity-0'` and NO `group-hover:` rule,
 *      while the prev arrow carried both. `isHovering` was set from
 *      `onMouseEnter` on this root — and every card that hosts this carousel
 *      lays a STRETCHED `CardOverlayLink` over its whole surface as a SIBLING,
 *      so the root never receives `mouseenter`. Except over an arrow itself,
 *      which is on the controls layer ABOVE the link and whose hover bubbles
 *      here: hence "the right arrow appears when you hover where it would be".
 *      The state and both handlers are gone; there is ONE visibility rule and
 *      both arrows carry it, so they cannot diverge again.
 *
 * ★ THAT ONE RULE IS NOT THIS FILE'S TO SPELL, AND THAT IS THE SECOND HALF
 * (§2.13). Making the reveal `group-hover:`-only made it depend on a class an
 * ANCESTOR carries — a contract between two files that no type stated. The
 * desktop map popover (`SelectedListingCard`) is a third host whose root was
 * hand-rolled and carried no `group`, so from #6839 its arrows were reachable
 * by Tab and by nothing else, and NOTHING was red: an invisible arrow renders
 * and passes. The rule now arrives from `useCardSurface()`, minted by the very
 * element that carries the `group`, so a host that did not open with
 * `CardSurface` gets a throw naming the missing root instead of an arrow no
 * pointer can find.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { useCardSurface } from '@letsgethome/ui/card-layers';
import { cn } from '@letsgethome/ui/lib/utils';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useRef, useState, type TouchEvent } from 'react';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export type CarouselSlide = {
  /** Stable per-slide React key. */
  key: string;
  /** Rendered slide content (fills its `relative w-full h-full` frame). */
  content: React.ReactNode;
};

interface SlideCarouselProps {
  /** Rendered slides, in display order. */
  slides: CarouselSlide[];
  /** Additional classes for container */
  className?: string;
  /** Fallback when no slides */
  fallback?: React.ReactNode;
}

/**
 * The arrow's geometry and chrome — everything about it EXCEPT which side it
 * sits on and when it shows. Shared so the two arrows cannot drift in size,
 * shape or treatment either.
 */
const ARROW_CLASS = [
  'absolute top-1/2 -translate-y-1/2 hidden md:flex',
  'items-center justify-center w-7 h-7 rounded-full',
  'bg-background/90 backdrop-blur-sm shadow-sm',
  'hover:bg-background hover:scale-110 transition-all',
].join(' ');

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export function SlideCarousel({ slides, className, fallback }: SlideCarouselProps) {
  const tA11y = useTranslations('common');
  // ONE visibility rule for BOTH arrows, and it comes from the element that
  // carries the `group` it keys off — never from a string this file owns. See
  // the ★ in the header: a rule spelled here can outlive the ancestor it needs.
  const { hoverRevealClassName } = useCardSurface();
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const maxDots = 5; // Show max 5 dots like Rails

  // The ONE question both arrows and the swipe ask, and the ONLY source of the
  // answer: where in the deck are we? An arrow renders iff it can act, so
  // "there is a previous photo" and "a previous arrow is on screen" are the
  // same fact rather than two that can drift.
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < slides.length - 1;

  // Navigate to previous slide (clamped — no wrap)
  const goToPrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  // Navigate to next slide (clamped — no wrap)
  const goToNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setCurrentIndex((prev) => Math.min(slides.length - 1, prev + 1));
    },
    [slides.length]
  );

  // Touch handlers for mobile swipe
  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (touchStartX.current === null) return;

      const touchEndX = e.changedTouches[0]?.clientX;
      if (touchEndX === undefined) return;
      const diff = touchStartX.current - touchEndX;
      const minSwipeDistance = 50;

      if (Math.abs(diff) > minSwipeDistance) {
        if (diff > 0) {
          // Swipe left → next (clamped at the last photo, like the arrow)
          setCurrentIndex((prev) => Math.min(slides.length - 1, prev + 1));
        } else {
          // Swipe right → prev (clamped at the first photo, like the arrow)
          setCurrentIndex((prev) => Math.max(0, prev - 1));
        }
      }

      touchStartX.current = null;
    },
    [slides.length]
  );

  // No slides - show fallback
  if (slides.length === 0) {
    return (
      <div
        className={cn(
          'w-full h-full flex items-center justify-center bg-muted text-muted-foreground',
          className
        )}
      >
        {fallback}
      </div>
    );
  }

  return (
    <div
      className={cn('relative w-full h-full', className)}
      role="presentation"
      onTouchStart={slides.length > 1 ? handleTouchStart : undefined}
      onTouchEnd={slides.length > 1 ? handleTouchEnd : undefined}
    >
      {/* Slides */}
      <div className="w-full h-full overflow-hidden">
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {slides.map((slide) => (
            <div key={slide.key} className="relative w-full h-full flex-shrink-0">
              {slide.content}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows - desktop only, on hover.
          On the CONTROLS layer because the cards that host this carousel lay a
          STRETCHED link over their whole surface (ListingCard, EstateCard —
          `CardOverlayLink`). None of the wrappers between here and that link
          creates a stacking context, so an arrow left at the `z-index: auto`
          default paints UNDER it and the click navigates instead of advancing
          the photo (LET-85). ⚠ That sentence used to spell the utility, and
          Tailwind's scanner reads source as TEXT — so it MINTED a live CSS
          rule for a class no element in the estate carries. See `z-layers`.
          The layer is `@letsgethome/ui/z-layers`' claim, not this file's:
          `Z_LAYERS.cardControls.z > Z_LAYERS.cardOverlayLink.z` is a tested
          value, and `no-raw-z-layer` bans a hand-picked `z-*` here. */}
      {canPrev && (
        <UnstyledButton
          onClick={goToPrev}
          className={cn(
            'left-2',
            ARROW_CLASS,
            Z_LAYERS.cardControls.className,
            hoverRevealClassName
          )}
          aria-label={tA11y('a11y.previousImage')}
        >
          <ChevronLeft className="h-4 w-4" />
        </UnstyledButton>
      )}
      {canNext && (
        <UnstyledButton
          onClick={goToNext}
          className={cn(
            'right-2',
            ARROW_CLASS,
            Z_LAYERS.cardControls.className,
            hoverRevealClassName
          )}
          aria-label={tA11y('a11y.nextImage')}
        >
          <ChevronRight className="h-4 w-4" />
        </UnstyledButton>
      )}

      {/* Dot indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {slides.slice(0, maxDots).map((slide, index) => (
            <div
              key={slide.key}
              className={cn(
                'w-1.5 h-1.5 rounded-full transition-all',
                index === currentIndex ? 'bg-background' : 'bg-background/50'
              )}
            />
          ))}
          {slides.length > maxDots && <div className="w-1.5 h-1.5 rounded-full bg-background/50" />}
        </div>
      )}
    </div>
  );
}
